/**
 * Create / edit an anticipated expense (draft or returned).
 *
 * Defaults come from the server (finance_expense_defaults): department from the payee, state from the event,
 * GL account from the department x billing category mapping, expected payment date from the payee's payment terms.
 * Changing the expected payment date away from the terms needs a reason; only finance can override the GL account
 * (also with a reason). The server re-derives and re-checks everything on save.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Modal from "../common/Modal";
import Button from "../common/Button";
import {
  financeErrorMessage,
  getExpenseDefaults,
  getFinanceLookups,
  payeeLabel,
  saveExpense,
  searchPayeeOptions,
} from "../../services/finance.service";
import SearchableSelect from "../common/SearchableSelect";
import type {
  Expense,
  ExpenseFields,
  PayeeType,
} from "../../types/finance.types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (e: Expense) => void;
  eventId: string | null;
  expense?: Expense | null;
  isFinanceAdmin: boolean;
}

const inputCls =
  "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50";
const labelCls = "block text-sm font-medium text-gray-700";

export default function ExpenseFormModal({
  isOpen,
  onClose,
  onSaved,
  eventId,
  expense,
  isFinanceAdmin,
}: Props) {
  const editing = !!expense;
  const { data: lookups, isLoading } = useQuery({
    queryKey: ["finance-lookups"],
    queryFn: getFinanceLookups,
    enabled: isOpen,
    staleTime: 60 * 1000,
  });

  // vendors only (owner 2026-10-22: employee expenses go through Concur)
  const payeeType: PayeeType = "vendor";
  const [typeCode, setTypeCode] = useState("");
  const [payeeId, setPayeeId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [stateTouched, setStateTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [serviceDate, setServiceDate] = useState("");
  const [payDate, setPayDate] = useState("");
  const [payTouched, setPayTouched] = useState(false);
  const [payReason, setPayReason] = useState("");
  const [glOverride, setGlOverride] = useState("");
  const [glReason, setGlReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setError(null);
    setTypeCode(expense?.expense_type_code ?? "");
    setPayeeId(expense?.payee_id ?? "");
    setDepartmentId(expense?.department_id ?? "");
    setCategoryId(expense?.billing_category_id ?? "");
    setStateCode(expense?.state ?? "");
    setStateTouched(!!expense);
    setDescription(expense?.description ?? "");
    setAmount(expense ? String(expense.anticipated_amount) : "");
    setServiceDate(expense?.anticipated_service_date ?? "");
    setPayDate(expense?.est_payment_date ?? "");
    setPayTouched(!!expense?.est_payment_overridden);
    setPayReason(expense?.est_payment_override_reason ?? "");
    setGlOverride(expense?.gl_overridden ? (expense.gl_account_no ?? "") : "");
    setGlReason(expense?.gl_override_reason ?? "");
  }, [isOpen, expense]);

  const { data: defaults } = useQuery({
    queryKey: [
      "finance-defaults",
      eventId,
      payeeId,
      departmentId,
      categoryId,
      typeCode,
      serviceDate,
    ],
    queryFn: () =>
      getExpenseDefaults({
        eventId,
        payeeId: payeeId || null,
        departmentId: departmentId || null,
        categoryId: categoryId || null,
        typeCode: typeCode || null,
        serviceDate: serviceDate || null,
      }),
    enabled: isOpen && (!!payeeId || !!eventId),
  });

  // Apply server defaults to untouched fields.
  useEffect(() => {
    if (!defaults) {
      return;
    }
    if (!departmentId && defaults.department_id) {
      setDepartmentId(defaults.department_id);
    }
    if (!stateTouched) {
      setStateCode(defaults.state ?? "");
    }
    if (!payTouched) {
      setPayDate(defaults.est_payment_date ?? "");
    }
  }, [defaults, departmentId, stateTouched, payTouched]);

  const payOverridden =
    !!defaults?.est_payment_date &&
    !!payDate &&
    payDate !== defaults.est_payment_date;
  const glUsed = glOverride.trim() || defaults?.gl_account_no || null;
  const glOverridden =
    !!glOverride.trim() &&
    glOverride.trim() !== (defaults?.gl_account_no ?? "");

  const handleSave = async () => {
    setError(null);
    if (
      !typeCode ||
      !payeeId ||
      !categoryId ||
      !description.trim() ||
      !amount ||
      !serviceDate
    ) {
      setError(
        "Fill in the expense type, payee, billing category, description, amount and service date.",
      );
      return;
    }
    if (payOverridden && !payReason.trim()) {
      setError("Give a reason for changing the expected payment date.");
      return;
    }
    if (glOverridden && !glReason.trim()) {
      setError("Give a reason for overriding the GL account.");
      return;
    }
    const fields: ExpenseFields = {
      expense_type_code: typeCode,
      payee_id: payeeId,
      department_id: departmentId || null,
      billing_category_id: categoryId,
      state: stateCode.trim().toUpperCase() || null,
      description: description.trim(),
      anticipated_amount: Number(amount),
      anticipated_service_date: serviceDate,
      est_payment_date: payDate || null,
      est_payment_override_reason: payOverridden ? payReason.trim() : null,
    };
    if (!editing) {
      fields.event_id = eventId;
    }
    if (isFinanceAdmin) {
      fields.gl_account_no = glOverridden ? glOverride.trim() : null;
      fields.gl_override_reason = glOverridden ? glReason.trim() : null;
    }
    setSaving(true);
    try {
      const saved = await saveExpense(
        expense?.id ?? null,
        expense?.row_version ?? null,
        fields,
      );
      onSaved(saved);
    } catch (e) {
      setError(financeErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const noMasterData =
    !!lookups &&
    (!lookups.hasActiveVendors ||
      lookups.categories.length === 0 ||
      lookups.departments.length === 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        editing ? `Edit ${expense?.expense_no}` : "Add anticipated expense"
      }
      size="lg"
    >
      {isLoading ? (
        <p className="py-6 text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="space-y-4" data-testid="finance-expense-form">
          {noMasterData && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Finance has not finished setting up payees, departments or billing
              categories yet, so some choices may be missing. Ask finance to
              complete Finance settings.
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="fx-type">
                Expense type
              </label>
              <select
                id="fx-type"
                className={inputCls}
                value={typeCode}
                onChange={(e) => setTypeCode(e.target.value)}
              >
                <option value="">Choose…</option>
                {(lookups?.types ?? [])
                  .filter(
                    (t) => t.is_active || t.code === expense?.expense_type_code,
                  )
                  .map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              {/* Server-side type-to-search (~9,800 vendors; a full list would stop at PostgREST's
                  1,000-row cap). Active vendors only; the current vendor of an edited expense stays
                  shown even if it has since been deactivated. */}
              <SearchableSelect
                label="Vendor"
                value={payeeId}
                onChange={(v) => {
                  setPayeeId(v);
                  if (!editing) {
                    setDepartmentId("");
                  }
                }}
                search={searchPayeeOptions}
                resolveLabel={payeeLabel}
                queryKeyBase="finance-payee-picker-expense"
                emptyLabel="Choose…"
                placeholder="Search by vendor name or vendor number..."
                testId="fx-payee"
              />
              {lookups && !lookups.hasActiveVendors && (
                <p className="mt-1 text-xs text-gray-500">
                  No {payeeType === "vendor" ? "vendors" : "employees"} set up
                  yet. Finance adds them in Finance settings.
                </p>
              )}
            </div>
            <div>
              <label className={labelCls} htmlFor="fx-dept">
                Department
              </label>
              <select
                id="fx-dept"
                className={inputCls}
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
              >
                <option value="">Choose…</option>
                {(lookups?.departments ?? [])
                  .filter((d) => d.is_active || d.id === expense?.department_id)
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Defaults from the payee. Decides who approves.
              </p>
            </div>
            <div>
              <label className={labelCls} htmlFor="fx-cat">
                Billing category
              </label>
              <select
                id="fx-cat"
                className={inputCls}
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">Choose…</option>
                {(lookups?.categories ?? [])
                  .filter(
                    (c) => c.is_active || c.id === expense?.billing_category_id,
                  )
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="fx-amount">
                Anticipated amount (USD)
              </label>
              <input
                id="fx-amount"
                type="number"
                min="0.01"
                step="0.01"
                className={inputCls}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="fx-service">
                Anticipated service date
              </label>
              <input
                id="fx-service"
                type="date"
                className={inputCls}
                value={serviceDate}
                onChange={(e) => setServiceDate(e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="fx-state">
                State
              </label>
              <input
                id="fx-state"
                maxLength={2}
                className={inputCls}
                value={stateCode}
                onChange={(e) => {
                  setStateTouched(true);
                  setStateCode(e.target.value.toUpperCase());
                }}
              />
              <p className="mt-1 text-xs text-gray-500">
                {eventId
                  ? "Defaults from the event."
                  : "Defaults from the payee."}
              </p>
            </div>
            <div>
              <label className={labelCls} htmlFor="fx-pay">
                Expected payment date
              </label>
              <input
                id="fx-pay"
                type="date"
                className={inputCls}
                value={payDate}
                onChange={(e) => {
                  setPayTouched(true);
                  setPayDate(e.target.value);
                }}
              />
              <p className="mt-1 text-xs text-gray-500">
                {defaults?.payment_terms_name
                  ? `From payment terms: ${defaults.payment_terms_name}${defaults.est_payment_date ? ` (${defaults.est_payment_date})` : ""}.`
                  : "This payee has no payment terms; enter the date if known."}
              </p>
            </div>
            {payOverridden && (
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="fx-pay-reason">
                  Why is the payment date different from the terms?
                </label>
                <input
                  id="fx-pay-reason"
                  className={inputCls}
                  value={payReason}
                  onChange={(e) => setPayReason(e.target.value)}
                />
              </div>
            )}
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="fx-desc">
                Description
              </label>
              <textarea
                id="fx-desc"
                rows={3}
                maxLength={2000}
                className={inputCls}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="sm:col-span-2 rounded-md bg-gray-50 p-3 text-sm">
              <span className="font-medium text-gray-700">GL account: </span>
              {glUsed ? (
                <span className="font-mono">{glUsed}</span>
              ) : (
                <span className="text-amber-700">
                  No GL mapping for this department and billing category yet.
                  You can save a draft; it cannot be submitted until finance
                  adds the mapping.
                </span>
              )}
              {isFinanceAdmin && (
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className={labelCls} htmlFor="fx-gl">
                      Override GL account (finance only)
                    </label>
                    <input
                      id="fx-gl"
                      className={inputCls}
                      value={glOverride}
                      onChange={(e) => setGlOverride(e.target.value)}
                    />
                  </div>
                  {glOverridden && (
                    <div>
                      <label className={labelCls} htmlFor="fx-gl-reason">
                        Reason for override
                      </label>
                      <input
                        id="fx-gl-reason"
                        className={inputCls}
                        value={glReason}
                        onChange={(e) => setGlReason(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {error && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Save as draft"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
